import { useCallback, useEffect, useRef, useState } from "react";
import {
  getPendingEnrollmentLetters,
  assignEnrollmentLetterUser,
  uploadEnrollmentLetter,
  getEnrollmentLetterHistory,
  searchStudentForEnrollmentLetter,
  markEnrollmentLetterPending,
  getUsers,
} from "../../actions/studensts";
import { excelEnrollmentLetterHistoryReport } from "../exportToExcel";
import AlertSuccess from "../../components/AlertSuccess";
import AlertDenger from "../../components/AlertDenger";
import PdfViewerModal from "../../components/PdfViewerModal";
import EnrollmentStatusBadge from "../../components/EnrollmentStatusBadge";
import Pagination from "../Table/Pagination";
import UNAUTHORIZED from "../UNAUTHORIZED";

function AddStudentPanel({ existingIds, onAdd }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);
  const [addingId, setAddingId] = useState(null);

  const handleAddClick = async (r) => {
    setAddingId(r.national_id);
    await onAdd(r);
    setAddingId(null);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;
    setSearching(true);
    setError("");
    setSearched(true);
    const res = await searchStudentForEnrollmentLetter(searchTerm.trim());
    setSearching(false);
    if (res && res.status === 200) {
      setResults(res.data.data || []);
    } else {
      setResults([]);
      setError(res?.data?.ERROR || "Search failed.");
    }
  };

  return (
    <div className="mb-5 p-4 border border-dashed border-gray-300 rounded-lg">
      <h3 className="font-semibold mb-2 text-gray-700">Add Student</h3>
      <form onSubmit={handleSearch} className="flex gap-2 mb-3">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="National ID, full name, or passport number"
          className="border border-gray-300 rounded-md px-3 py-2 w-64 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <button
          type="submit"
          disabled={searching || !searchTerm.trim()}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
        >
          {searching ? "Searching..." : "Search"}
        </button>
      </form>

      {error && <p className="text-red-500 text-xs mb-2">{error}</p>}

      {searched && !searching && results.length === 0 && !error && (
        <p className="text-gray-400 text-sm">No student found matching that search.</p>
      )}

      {results.length > 0 && (
        <div className="flex flex-col gap-2">
          {results.map((r) => {
            const alreadyAdded = existingIds.includes(r.national_id);
            // Already "No" means this student already matches the pending
            // filter — they don't need to be added, whether or not they
            // happen to be on the currently loaded page/search of the
            // Pending list below.
            const alreadyPending =
              r.enrollment_letter_received === 0 || r.enrollment_letter_received === "0";
            const disableAdd = alreadyAdded || alreadyPending || addingId === r.national_id;
            const label = alreadyAdded
              ? "Already Added"
              : alreadyPending
              ? "Already Pending"
              : addingId === r.national_id
              ? "Adding..."
              : "Add";
            return (
              <div
                key={r.national_id}
                className="flex items-center justify-between gap-3 border border-gray-200 rounded-md px-3 py-2"
              >
                <div className="flex items-center gap-3 text-sm">
                  <span className="font-medium">{r.full_name}</span>
                  <span className="text-gray-500">{r.national_id}</span>
                  <span className="text-gray-500">{r.state || "—"}</span>
                  <EnrollmentStatusBadge value={r.enrollment_letter_received} nullLabel="Not Set" />
                </div>
                <button
                  type="button"
                  disabled={disableAdd}
                  onClick={() => handleAddClick(r)}
                  className="px-3 py-1.5 rounded-md text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {label}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PendingRow({ student, users, username, onUploaded }) {
  const [assignedUser, setAssignedUser] = useState(student.enrollment_letter_assigned_user || "");
  const [assigning, setAssigning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const handleAssign = async (e) => {
    const value = e.target.value;
    setAssignedUser(value);
    setAssigning(true);
    setError("");
    const res = await assignEnrollmentLetterUser(student.national_id, value);
    setAssigning(false);
    if (!res || res.status !== 200) {
      setError(res?.data?.ERROR || "Failed to assign.");
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!assignedUser) {
      setError("Assign this student to a user before uploading.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setUploading(true);
    setError("");
    const formData = new FormData();
    formData.append("id", student.national_id);
    formData.append("uploaded_by", username || "");
    formData.append("enrollment_letter_file", file);
    const res = await uploadEnrollmentLetter(formData);
    setUploading(false);
    if (res && res.status === 200) {
      onUploaded(student.national_id);
    } else {
      setError(res?.data?.ERROR || "Failed to upload.");
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <tr className="hover:bg-blue-50 transition-colors">
      <td className="px-4 py-2 border border-gray-200">{student.national_id}</td>
      <td className="px-4 py-2 border border-gray-200 font-medium">{student.full_name}</td>
      <td className="px-4 py-2 border border-gray-200">{student.passport_number || "—"}</td>
      <td className="px-4 py-2 border border-gray-200">{student.state || "—"}</td>
      <td className="px-4 py-2 border border-gray-200">
        <select
          value={assignedUser}
          onChange={handleAssign}
          disabled={assigning}
          className="border border-gray-300 rounded-md px-2 py-1 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          <option value="">Unassigned</option>
          {users.map((u) => (
            <option key={u.username} value={u.username}>
              {u.username}
            </option>
          ))}
        </select>
      </td>
      <td className="px-4 py-2 border border-gray-200 text-center">
        <label
          title={!assignedUser ? "Assign this student to a user first" : undefined}
          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white
            ${uploading || !assignedUser ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700 cursor-pointer"}`}
        >
          {uploading ? "Uploading..." : "Upload PDF"}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            disabled={uploading || !assignedUser}
            onChange={handleFileChange}
          />
        </label>
        {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
      </td>
    </tr>
  );
}

function HistoryRow({ record }) {
  const [showPdfModal, setShowPdfModal] = useState(false);

  return (
    <tr className="hover:bg-blue-50 transition-colors">
      <td className="px-4 py-2 border border-gray-200">{record.national_id}</td>
      <td className="px-4 py-2 border border-gray-200 font-medium">{record.full_name}</td>
      <td className="px-4 py-2 border border-gray-200">{record.passport_number || "—"}</td>
      <td className="px-4 py-2 border border-gray-200">{record.state || "—"}</td>
      <td className="px-4 py-2 border border-gray-200">{record.enrollment_letter_assigned_user || "—"}</td>
      <td className="px-4 py-2 border border-gray-200">{record.enrollment_letter_uploaded_by || "—"}</td>
      <td className="px-4 py-2 border border-gray-200">{record.enrollment_letter_date || "—"}</td>
      <td className="px-4 py-2 border border-gray-200 text-center">
        <button
          type="button"
          onClick={() => setShowPdfModal(true)}
          className="px-3 py-1.5 rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          View PDF
        </button>
        {showPdfModal && (
          <PdfViewerModal
            fileName={record.enrollment_letter_file}
            onClose={() => setShowPdfModal(false)}
          />
        )}
      </td>
    </tr>
  );
}

const ENROLLMENT_LETTERS_USERS = ["acountant-1", "admin", "asmaa", "Yara", "abdelrhman", "alaa"];

function UploadEnrollmentLetters() {
  const userProfile = JSON.parse(localStorage.getItem("profile"))?.value?.[0];
  const canAccess = ENROLLMENT_LETTERS_USERS.includes(userProfile?.username);

  const [tab, setTab] = useState("pending");

  // Pending tab state
  const [students, setStudents] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [maxPage, setMaxPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // History tab state
  const [history, setHistory] = useState([]);
  const [historySearch, setHistorySearch] = useState("");
  const [historyPage, setHistoryPage] = useState(1);
  const [historyMaxPage, setHistoryMaxPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const flash = (type, msg) => {
    if (type === "success") {
      setSuccessMsg(msg);
      setTimeout(() => setSuccessMsg(""), 2500);
    } else {
      setErrorMsg(msg);
      setTimeout(() => setErrorMsg(""), 3000);
    }
  };

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    const res = await getPendingEnrollmentLetters(search, page);
    if (res && res.status === 200) {
      setStudents(res.data.data || []);
      setMaxPage(res.data.count?.maxPage || 1);
    } else {
      flash("error", "Failed to load students.");
    }
    setLoading(false);
  }, [search, page]);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    const res = await getEnrollmentLetterHistory(historySearch, historyPage);
    if (res && res.status === 200) {
      setHistory(res.data.data || []);
      setHistoryMaxPage(res.data.count?.maxPage || 1);
    } else {
      flash("error", "Failed to load history.");
    }
    setHistoryLoading(false);
  }, [historySearch, historyPage]);

  useEffect(() => {
    if (!canAccess || tab !== "pending") return;
    fetchStudents();
  }, [fetchStudents, canAccess, tab]);

  useEffect(() => {
    if (!canAccess || tab !== "history") return;
    fetchHistory();
  }, [fetchHistory, canAccess, tab]);

  useEffect(() => {
    if (!canAccess) return;
    (async () => {
      const res = await getUsers();
      // /users responds 201, not 200 — check data presence rather than a
      // specific status code.
      if (res && res.data?.users) setUsers(res.data.users);
    })();
  }, [canAccess]);

  const handleUploaded = (national_id) => {
    setStudents((prev) => prev.filter((s) => s.national_id !== national_id));
    flash("success", "Enrollment letter uploaded — student marked as submitted.");
  };

  const handleAddStudent = async (result) => {
    // Force the student's status to "No" so they actually match the pending
    // filter and stay in the list after a refresh, regardless of what their
    // status was before being pulled in here.
    const res = await markEnrollmentLetterPending(result.national_id);
    if (!res || res.status !== 200) {
      flash("error", res?.data?.ERROR || "Failed to add student.");
      return;
    }
    setStudents((prev) =>
      prev.some((s) => s.national_id === result.national_id)
        ? prev
        : [{ ...result, enrollment_letter_received: 0 }, ...prev]
    );
    flash("success", `${result.full_name} added to the list below.`);
  };

  const handleExportHistory = async () => {
    setExporting(true);
    try {
      await excelEnrollmentLetterHistoryReport(historySearch);
    } catch {
      flash("error", "Failed to export Excel file.");
    }
    setExporting(false);
  };

  if (!canAccess) return <UNAUTHORIZED />;

  return (
    <div className="p-5">
      <h1 className="text-2xl text-white pb-5 pl-1">Upload Enrollment Letters</h1>

      {successMsg && <AlertSuccess text={successMsg} />}
      {errorMsg && <AlertDenger text={errorMsg} />}

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-4 border-b border-gray-200">
          <button
            type="button"
            onClick={() => setTab("pending")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === "pending"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Pending
          </button>
          <button
            type="button"
            onClick={() => setTab("history")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === "history"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            History
          </button>
        </div>

        {tab === "pending" && (
          <>
            <AddStudentPanel
              existingIds={students.map((s) => s.national_id)}
              onAdd={handleAddStudent}
            />

            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="text-xl font-semibold">
                Students Pending Enrollment Letter{" "}
                <span className="text-gray-500 text-base font-normal">({students.length})</span>
              </h2>
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
                placeholder="Search by full name, national ID or passport number..."
                className="border border-gray-300 rounded-md px-3 py-2 w-80 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            {loading && <p className="text-gray-500 text-center py-6">Loading...</p>}

            {!loading && students.length === 0 && (
              <p className="text-gray-400 text-center py-6">
                No students pending an enrollment letter.
              </p>
            )}

            {!loading && students.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-100 text-left">
                      <th className="px-4 py-2 border border-gray-200">National ID</th>
                      <th className="px-4 py-2 border border-gray-200">Full Name</th>
                      <th className="px-4 py-2 border border-gray-200">Passport Number</th>
                      <th className="px-4 py-2 border border-gray-200">State</th>
                      <th className="px-4 py-2 border border-gray-200">Assigned To</th>
                      <th className="px-4 py-2 border border-gray-200 text-center">Upload</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student) => (
                      <PendingRow
                        key={student.national_id}
                        student={student}
                        users={users}
                        username={userProfile?.username}
                        onUploaded={handleUploaded}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="py-4">
              <Pagination
                page={page}
                setPage={setPage}
                maxPage={maxPage}
                nextDisabled={page >= maxPage}
              />
            </div>
          </>
        )}

        {tab === "history" && (
          <>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="text-xl font-semibold">
                Uploaded Enrollment Letters{" "}
                <span className="text-gray-500 text-base font-normal">({history.length})</span>
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={handleExportHistory}
                  disabled={exporting}
                  className="inline-flex items-center gap-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
                >
                  📊 {exporting ? "Exporting..." : "Export Excel"}
                </button>
                <input
                  type="text"
                  value={historySearch}
                  onChange={(e) => {
                    setHistoryPage(1);
                    setHistorySearch(e.target.value);
                  }}
                  placeholder="Search by full name, national ID or passport number..."
                  className="border border-gray-300 rounded-md px-3 py-2 w-80 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            </div>

            {historyLoading && <p className="text-gray-500 text-center py-6">Loading...</p>}

            {!historyLoading && history.length === 0 && (
              <p className="text-gray-400 text-center py-6">
                No enrollment letters have been uploaded yet.
              </p>
            )}

            {!historyLoading && history.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-100 text-left">
                      <th className="px-4 py-2 border border-gray-200">National ID</th>
                      <th className="px-4 py-2 border border-gray-200">Full Name</th>
                      <th className="px-4 py-2 border border-gray-200">Passport Number</th>
                      <th className="px-4 py-2 border border-gray-200">State</th>
                      <th className="px-4 py-2 border border-gray-200">Assigned To</th>
                      <th className="px-4 py-2 border border-gray-200">Uploaded By</th>
                      <th className="px-4 py-2 border border-gray-200">Uploaded At</th>
                      <th className="px-4 py-2 border border-gray-200 text-center">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((record) => (
                      <HistoryRow key={record.national_id} record={record} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="py-4">
              <Pagination
                page={historyPage}
                setPage={setHistoryPage}
                maxPage={historyMaxPage}
                nextDisabled={historyPage >= historyMaxPage}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default UploadEnrollmentLetters;
